/**
 * Pure view-model logic for the lane queue visualisation.
 *
 * Kept free of React so the ordering / collapsing / counting rules can be
 * tested directly. The component beside it renders whatever this produces.
 *
 * THE ONE RULE THAT MATTERS: counts come from `lane.totals` (unbounded), tiles
 * come from `lane.batches` (a bounded window). Deriving a count from the window
 * makes a 703-batch run report as the 24 batches that happened to fit — the
 * precise lie this view exists to kill. See types/autocoderQueue.ts and the
 * server's own `note`.
 */
import type {
  QueueBatch,
  QueueBatchState,
  QueueLane,
  QueueTotals,
} from '../../types/autocoderQueue'

/**
 * Maximum number of individual tiles rendered per lane. Older *completed*
 * batches beyond this are folded into a single "+N done" summary tile so a
 * long-running lane cannot blow the layout; the live end (failed, running,
 * pending, and anything in a state we do not recognise) is never collapsed.
 */
export const DEFAULT_MAX_TILES = 24

/**
 * Reading order of the queue: done -> running -> pending -> unknown.
 * An unrecognised state sorts LAST rather than producing a NaN comparator.
 */
const STATE_GROUP: Record<string, number> = {
  completed: 0,
  failed: 0,
  running: 1,
  pending: 2,
}
const UNKNOWN_GROUP = 3

const groupOf = (state: QueueBatchState): number => STATE_GROUP[state] ?? UNKNOWN_GROUP

const createdAtMs = (b: QueueBatch): number => {
  const t = Date.parse(b.createdAt)
  return Number.isNaN(t) ? 0 : t
}

/**
 * Orders a lane's batches so movement is legible: terminal batches on the
 * left, then what is running, then what is waiting. Within each group, oldest
 * first. Never mutates the input.
 */
export function orderLaneBatches(batches: QueueBatch[]): QueueBatch[] {
  return [...batches].sort((a, b) => {
    const g = groupOf(a.state) - groupOf(b.state)
    if (g !== 0) return g
    const t = createdAtMs(a) - createdAtMs(b)
    if (t !== 0) return t
    return a.id.localeCompare(b.id)
  })
}

export interface LaneView {
  lane: string
  displayName: string
  inFlightCap: number
  /** False when the server reports no limiter seeded for this lane here. */
  capKnown: boolean
  // ---- every count below is UNBOUNDED (from lane.totals) ------------------
  runningCount: number
  pendingCount: number
  completedCount: number
  failedCount: number
  /** Batches in a state this build does not know about. Never hidden. */
  otherCount: number
  /** Every batch this lane holds, in any state. */
  totalBatches: number
  /** Records still waiting to be coded in this lane. */
  pendingRecords: number
  /** e.g. "2 of 2 in flight" — the reason only two run at once. */
  capLabel: string
  atCap: boolean
  isEmpty: boolean
  /** Completed batches not drawn as tiles — measured against the unbounded total. */
  collapsedDoneCount: number
  /** The batches to render as tiles, already in queue order. */
  tiles: QueueBatch[]
}

export interface BuildLaneViewOptions {
  maxTiles?: number
}

/**
 * Counts derived from the window. Used ONLY when the server sent no totals at
 * all (an older build): a window-derived count understates, but a blank lane
 * would be worse. Everything else reads `lane.totals`.
 */
const totalsFromWindow = (batches: QueueBatch[]): QueueTotals => {
  const t: QueueTotals = {
    pending: 0, running: 0, completed: 0, failed: 0, other: 0,
    batches: batches.length, records: 0, pendingRecords: 0, runningRecords: 0,
  }
  for (const b of batches) {
    const n = b.records ?? 0
    t.records += n
    switch (b.state) {
      case 'pending': t.pending++; t.pendingRecords += n; break
      case 'running': t.running++; t.runningRecords += n; break
      case 'completed': t.completed++; break
      case 'failed': t.failed++; break
      default: t.other++; break
    }
  }
  return t
}

export function buildLaneView(lane: QueueLane, opts: BuildLaneViewOptions = {}): LaneView {
  const maxTiles = opts.maxTiles ?? DEFAULT_MAX_TILES
  const batches = lane.batches ?? []
  const totals = lane.totals ?? totalsFromWindow(batches)

  // Tiles: only completed batches may be folded away. Everything else — a
  // failure, a running batch, a state we do not recognise — is kept, because
  // folding it away is how an operator stops seeing a problem.
  const completed = batches.filter((b) => b.state === 'completed')
  const mustKeep = batches.filter((b) => b.state !== 'completed')
  const slotsLeft = Math.max(0, maxTiles - mustKeep.length)

  const completedNewestFirst = [...completed].sort((a, b) => createdAtMs(b) - createdAtMs(a))
  const keptCompleted = completedNewestFirst.slice(0, slotsLeft)

  // Measured against the UNBOUNDED completed count: the fold-away summary must
  // account for the history the window never carried, not just what it dropped.
  const collapsedDoneCount = Math.max(0, totals.completed - keptCompleted.length)

  const runningCount = totals.running
  const cap = lane.inFlightCap
  const capKnown = cap > 0

  return {
    lane: lane.lane,
    displayName: lane.displayName,
    inFlightCap: cap,
    capKnown,
    runningCount,
    pendingCount: totals.pending,
    completedCount: totals.completed,
    failedCount: totals.failed,
    otherCount: totals.other,
    totalBatches: totals.batches,
    pendingRecords: totals.pendingRecords,
    // A cap of 0 is "no limiter seeded for this lane in this process", not a
    // concurrency of zero — "3 of 0 in flight" would be nonsense.
    capLabel: capKnown
      ? `${runningCount} of ${cap} in flight`
      : `${runningCount} in flight · no limiter for this lane here`,
    atCap: capKnown && runningCount >= cap,
    // Emptiness is a fact about the LANE, not about the window: a lane whose
    // batches all fall outside the bound still has work.
    isEmpty: totals.batches === 0 && batches.length === 0,
    collapsedDoneCount,
    tiles: orderLaneBatches([...keptCompleted, ...mustKeep]),
  }
}

/** Human labels for the states this build knows about. */
const STATE_LABELS: Record<string, string> = {
  completed: 'Completed',
  running: 'Running',
  pending: 'Waiting',
  failed: 'Failed',
}

/** Glyphs — shape carries the meaning, colour only reinforces it. */
const STATE_GLYPHS: Record<string, string> = {
  completed: '✓',
  running: '▶',
  pending: '▢',
  failed: '✕',
}

/**
 * Label for a batch state. An unrecognised state renders as ITSELF: an
 * operator who sees "quarantined" has learned something; one who sees
 * "undefined" has only learned that we have a bug.
 */
export const stateLabel = (state: QueueBatchState): string => STATE_LABELS[state] ?? state

export const stateGlyph = (state: QueueBatchState): string => STATE_GLYPHS[state] ?? '•'

/**
 * The bounded-window disclosure. `batches[]` is a slice; saying so is the
 * difference between "this is the queue" and "this is the part of the queue we
 * drew". Returns null when there is nothing to disclose.
 */
export const windowNote = (
  shownBatches: number,
  totals: QueueTotals | undefined,
  batchLimit: number | undefined,
): string | null => {
  if (!totals || !totals.batches || shownBatches >= totals.batches) return null
  const bound = batchLimit && batchLimit > 0 ? ` of up to ${batchLimit.toLocaleString()}` : ''
  return `Showing ${shownBatches.toLocaleString()} of ${totals.batches.toLocaleString()} batches — ` +
    `a bounded window${bound}, newest work at the live end.`
}

export function formatTimestamp(value?: string | null): string | null {
  if (!value) return null
  const t = new Date(value)
  if (Number.isNaN(t.getTime())) return null
  return t.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** Duration between two timestamps, as a compact "1m 04s" style string. */
export function formatDuration(from?: string | null, to?: string | null): string | null {
  if (!from || !to) return null
  const a = Date.parse(from)
  const b = Date.parse(to)
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return null
  const secs = Math.round((b - a) / 1000)
  if (secs < 60) return `${secs}s`
  const m = Math.floor(secs / 60)
  const s = secs % 60
  return `${m}m ${String(s).padStart(2, '0')}s`
}
